/**
 * RecordingCompositor — composites WebGL canvas + DOM overlays into a
 * single 2D canvas that MediaRecorder can capture.
 *
 * Why this exists:
 *   `HTMLCanvasElement.captureStream()` only sees the canvas's own
 *   framebuffer. The Now Playing overlay, watermark, and image overlay are
 *   DOM elements layered *over* the WebGL canvas, so they were silently
 *   dropped from every recording.
 *
 * How it works:
 *   Each rAF tick (while a recording is active), we:
 *     1. drawImage(webglCanvas) into our 2D canvas at native resolution
 *     2. drawImage(imageOverlay) honoring its scale / position / blend / opacity
 *     3. Render Now Playing text + album art via Canvas2D
 *     4. Render watermark (handle text + optional logo) via Canvas2D
 *
 *   MediaRecorder reads from `compositor.captureStream()` instead of the
 *   raw WebGL canvas, so overlays land in the saved file.
 *
 * Font sizing: CSS uses `clamp(min, Nvw, max)`. We replicate that with
 * `vw = canvas.clientWidth / 100`, then scale to the canvas's drawing
 * buffer (so output looks the same as on screen at any DPR).
 */

import type {
  ImageOverlay,
  ImageOverlayConfig,
} from "../viz/imageOverlay";
import type {
  FontFamily,
  NowPlayingOverlay,
  NowPlayingRenderState,
} from "../ui/nowPlaying";

export interface CompositorOverlays {
  nowPlaying?: NowPlayingOverlay | null;
  imageOverlay?: ImageOverlay | null;
}

export class RecordingCompositor {
  private outCanvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private rafId: number | null = null;
  private active = false;
  private imgCache = new Map<string, HTMLImageElement>();

  constructor(
    private readonly sourceCanvas: HTMLCanvasElement,
    private readonly overlays: CompositorOverlays = {},
  ) {
    this.outCanvas = document.createElement("canvas");
    const ctx = this.outCanvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("RecordingCompositor: 2D context unavailable");
    this.ctx = ctx;
    this.syncSize();
  }

  /** Render-loop is on while recording so the captureStream stays live. */
  start(): void {
    if (this.active) return;
    this.active = true;
    const loop = (): void => {
      if (!this.active) return;
      this.renderFrame();
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    this.active = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /** Stream the composited canvas at `fps`. Use this in MediaRecorder. */
  captureStream(fps = 60): MediaStream {
    return this.outCanvas.captureStream(fps);
  }

  /** Composite once and return a PNG blob. Used by the Snapshot button. */
  snapshot(): Promise<Blob | null> {
    this.renderFrame();
    return new Promise((resolve) => {
      this.outCanvas.toBlob((b) => resolve(b), "image/png");
    });
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private syncSize(): void {
    const w = this.sourceCanvas.width || 1280;
    const h = this.sourceCanvas.height || 720;
    if (this.outCanvas.width !== w || this.outCanvas.height !== h) {
      this.outCanvas.width = w;
      this.outCanvas.height = h;
    }
  }

  /** CSS-pixel → device-pixel scale (handles DPR + scaled canvases). */
  private get scale(): number {
    const cssW = this.sourceCanvas.clientWidth || this.sourceCanvas.width;
    if (cssW <= 0) return 1;
    return this.sourceCanvas.width / cssW;
  }

  /** 1vw in *device* pixels. Mirrors the CSS clamp() expressions. */
  private get vw(): number {
    return this.outCanvas.width / 100;
  }

  private renderFrame(): void {
    this.syncSize();
    const ctx = this.ctx;
    const w = this.outCanvas.width;
    const h = this.outCanvas.height;

    // Solid black backdrop — matches the page's #000 fallback so recordings
    // never show transparent pixels.
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, w, h);

    // 1. The visualizer itself.
    try {
      ctx.drawImage(this.sourceCanvas, 0, 0, w, h);
    } catch {
      /* canvas may briefly be 0x0 during resize; skip silently */
    }

    // 2. Image overlay (DOM <img>) — drawn directly so blend mode + opacity
    //    + scale match what the user sees.
    if (this.overlays.imageOverlay) {
      this.drawImageOverlay(this.overlays.imageOverlay.getRenderState());
    }

    // 3 & 4. Now Playing block + watermark.
    if (this.overlays.nowPlaying) {
      const s = this.overlays.nowPlaying.getRenderState();
      if (s.enabled) this.drawNowPlaying(s);
      if (s.watermarkEnabled) this.drawWatermark(s);
    }

    // Reset state so subsequent frames start clean.
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  }

  private getImage(src: string): HTMLImageElement | null {
    if (!src) return null;
    let img = this.imgCache.get(src);
    if (!img) {
      img = new Image();
      img.src = src;
      this.imgCache.set(src, img);
    }
    return img.complete && img.naturalWidth > 0 ? img : null;
  }

  // -------------------------------------------------------------------------
  // Image overlay (DOM <img> with scale / position / blend / opacity)
  // -------------------------------------------------------------------------

  private drawImageOverlay(cfg: ImageOverlayConfig): void {
    if (!cfg.visible || !cfg.src) return;
    const img = this.getImage(cfg.src);
    if (!img) return;
    const ctx = this.ctx;
    const w = this.outCanvas.width;
    const h = this.outCanvas.height;

    // Match CSS: image is its natural pixel size scaled by `cfg.scale`,
    // then translated by (-50%, -50%) and positioned by posX/posY.
    const dw = img.naturalWidth * cfg.scale * this.scale;
    const dh = img.naturalHeight * cfg.scale * this.scale;
    const cx = cfg.posX * w;
    const cy = cfg.posY * h;

    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, cfg.opacity));
    if (cfg.blendMode !== "normal") {
      ctx.globalCompositeOperation = cfg.blendMode;
    }
    ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
    ctx.restore();
  }

  // -------------------------------------------------------------------------
  // Now Playing block (title / artist / album / lyrics + optional album art)
  // -------------------------------------------------------------------------

  private drawNowPlaying(s: NowPlayingRenderState): void {
    const ctx = this.ctx;
    const w = this.outCanvas.width;
    const h = this.outCanvas.height;
    const sc = this.scale;
    const fam = familyFor(s.fontFamily);

    // CSS: clamp(...). We compute in device pixels.
    const titleSize = clampPx(20, 3.4 * this.vw, 38, sc);
    const artistSize = clampPx(13, 1.7 * this.vw, 18, sc);
    const albumSize = clampPx(10, 1.2 * this.vw, 13, sc);
    const lyricNowSize = clampPx(15, 2.0 * this.vw, 22, sc);
    const lyricPrevSize = clampPx(11, 1.3 * this.vw, 14, sc);
    const lyricNextSize = clampPx(11, 1.4 * this.vw, 15, sc);
    const titleWeight = weightFor(s.fontWeight);
    const lineGap = 4 * sc;
    const titleArtistGap = 4 * sc;
    const lyricStackTopGap = 14 * sc;

    const title = applyCase(s.title, s.uppercase);
    const artist = applyCase(s.artist, s.uppercase);
    const album = (s.album || "").toUpperCase(); // album is uppercase in CSS
    const lyricPrev = applyCase(s.lyricPrev, s.uppercase);
    const lyricNow = applyCase(s.lyricNow, s.uppercase);
    const lyricNext = applyCase(s.lyricNext, s.uppercase);

    // Album art square
    const artImg = s.albumArt ? this.getImage(s.albumArt) : null;
    const artSize = clampPx(56, 8 * this.vw, 96, sc);

    // Lay out each line: { text, font, color, size }.
    interface Line {
      text: string;
      font: string;
      size: number;
      fill: string;
      glow?: string;
      glowBlur?: number;
    }
    const blockLines: Line[] = [];
    if (title) {
      blockLines.push({
        text: title,
        font: cssFont(titleWeight, titleSize, fam),
        size: titleSize,
        fill: "#ffffff",
        glow: "rgba(0,0,0,0.55)",
        glowBlur: 18 * sc,
      });
    }
    if (artist) {
      blockLines.push({
        text: artist,
        font: cssFont(500, artistSize, fam),
        size: artistSize,
        fill: "rgba(255,255,255,0.92)",
        glow: "rgba(0,0,0,0.5)",
        glowBlur: 12 * sc,
      });
    }
    if (album) {
      blockLines.push({
        text: album,
        font: cssFont(400, albumSize, fam),
        size: albumSize,
        fill: "rgba(255,255,255,0.65)",
        glow: "rgba(0,0,0,0.4)",
        glowBlur: 10 * sc,
      });
    }

    const lyricLines: Line[] = [];
    if (s.showLyrics) {
      if (s.lyricMode === "karaoke" && lyricPrev) {
        lyricLines.push({
          text: lyricPrev,
          font: cssFont(400, lyricPrevSize, fam),
          size: lyricPrevSize,
          fill: "rgba(244,251,255,0.42)",
          glow: "rgba(0,0,0,0.55)",
          glowBlur: 6 * sc,
        });
      }
      if (lyricNow) {
        lyricLines.push({
          text: lyricNow,
          font: cssFont(s.lyricMode === "karaoke" ? 600 : 500, lyricNowSize, fam),
          size: lyricNowSize,
          fill: "#f4fbff",
          glow: "rgba(125,249,255,0.45)",
          glowBlur: 18 * sc,
        });
      }
      if (s.lyricMode === "karaoke" && lyricNext) {
        lyricLines.push({
          text: lyricNext,
          font: cssFont(400, lyricNextSize, fam),
          size: lyricNextSize,
          fill: "rgba(244,251,255,0.55)",
          glow: "rgba(0,0,0,0.55)",
          glowBlur: 6 * sc,
        });
      }
    }

    if (blockLines.length === 0 && lyricLines.length === 0 && !artImg) return;

    // Total content height (text column).
    const blockTextHeight = blockLines.reduce(
      (sum, l, i) => sum + l.size * 1.25 + (i > 0 ? titleArtistGap : 0),
      0,
    );
    const lyricStackHeight = lyricLines.reduce(
      (sum, l, i) => sum + l.size * 1.25 + (i > 0 ? lineGap : 0),
      0,
    );
    const lyricGap = lyricLines.length > 0 ? lyricStackTopGap : 0;
    const textColHeight = blockTextHeight + lyricGap + lyricStackHeight;
    const contentHeight = artImg
      ? Math.max(textColHeight, artSize)
      : textColHeight;
    const artGap = artImg ? 12 * sc : 0;
    const artWidth = artImg ? artSize + artGap : 0;

    // Anchor + alignment for each position.
    const margin = { x: 0.04 * w, y: 0.06 * h };
    let originX: number;
    let originY: number;
    let textAlign: CanvasTextAlign;
    let stackJustify: "start" | "end" | "center";
    switch (s.position) {
      case "bottom-left":
        originX = margin.x;
        originY = h - margin.y - contentHeight;
        textAlign = "left";
        stackJustify = "start";
        break;
      case "bottom-right":
        originX = w - margin.x;
        originY = h - margin.y - contentHeight;
        textAlign = "right";
        stackJustify = "end";
        break;
      case "top-left":
        originX = margin.x;
        originY = margin.y;
        textAlign = "left";
        stackJustify = "start";
        break;
      case "top-right":
        originX = w - margin.x;
        originY = margin.y;
        textAlign = "right";
        stackJustify = "end";
        break;
      case "center":
      default:
        originX = w / 2;
        originY = h / 2 - contentHeight / 2;
        textAlign = "center";
        stackJustify = "center";
        break;
    }

    ctx.save();
    ctx.textBaseline = "top";

    // Draw album art (left of text for left positions, right for right positions).
    if (artImg) {
      let artX: number;
      if (textAlign === "left") {
        artX = originX;
      } else if (textAlign === "right") {
        artX = originX - artSize;
      } else {
        artX = originX - artSize / 2;
      }
      // Slight drop shadow to match CSS.
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.55)";
      ctx.shadowBlur = 24 * sc;
      ctx.shadowOffsetY = 6 * sc;
      // Rounded corners (8px in CSS).
      const r = 8 * sc;
      roundedClip(ctx, artX, originY, artSize, artSize, r);
      ctx.drawImage(artImg, artX, originY, artSize, artSize);
      ctx.restore();
      ctx.restore();
      ctx.save();
      ctx.textBaseline = "top";
    }

    // Compute text column origin (offset by album art if present).
    let textX = originX;
    if (artImg) {
      if (textAlign === "left") textX = originX + artWidth;
      else if (textAlign === "right") textX = originX - artWidth;
      // For center we keep textX at center (album art is above text in the
      // CSS center variant; we keep it left-of-text for simplicity).
    }

    // Render block lines (title / artist / album).
    let cursorY = originY;
    ctx.textAlign = textAlign;
    for (let i = 0; i < blockLines.length; i++) {
      const line = blockLines[i];
      drawTextWithGlow(ctx, line.text, textX, cursorY, line.font, line.fill, line.glow, line.glowBlur);
      cursorY += line.size * 1.25 + (i < blockLines.length - 1 ? titleArtistGap : 0);
    }

    // Render lyric stack.
    if (lyricLines.length > 0) {
      cursorY += lyricStackTopGap;
      // Lyric stack alignment can differ from the block (CSS justifies lyric
      // stack right for bottom-right / top-right; centered for center).
      const lyricAlign: CanvasTextAlign =
        stackJustify === "start"
          ? "left"
          : stackJustify === "end"
            ? "right"
            : "center";
      ctx.textAlign = lyricAlign;
      let lyricX = textX;
      // For bottom-right / top-right, lyric stack hugs the right edge.
      if (lyricAlign === "right") lyricX = originX;
      else if (lyricAlign === "center") lyricX = originX;
      else lyricX = textX;
      for (let i = 0; i < lyricLines.length; i++) {
        const line = lyricLines[i];
        drawTextWithGlow(
          ctx,
          line.text,
          lyricX,
          cursorY,
          line.font,
          line.fill,
          line.glow,
          line.glowBlur,
        );
        cursorY += line.size * 1.25 + (i < lyricLines.length - 1 ? lineGap : 0);
      }
    }

    ctx.restore();
  }

  // -------------------------------------------------------------------------
  // Watermark (handle text + optional logo, in a pill at corner / bottom-center)
  // -------------------------------------------------------------------------

  private drawWatermark(s: NowPlayingRenderState): void {
    const handle = s.watermarkHandle;
    const logoImg = s.watermarkLogo ? this.getImage(s.watermarkLogo) : null;
    if (!handle && !logoImg) return;

    const ctx = this.ctx;
    const w = this.outCanvas.width;
    const h = this.outCanvas.height;
    const sc = this.scale;

    const fontSize = clampPx(11, 1.3 * this.vw, 14, sc);
    const logoSize = clampPx(20, 2.4 * this.vw, 28, sc);
    const padX = 12 * sc;
    const padY = 8 * sc;
    const gap = 8 * sc;
    const radius = 999;

    ctx.save();
    ctx.font = cssFont(
      600,
      fontSize,
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    );
    ctx.textBaseline = "middle";

    const textW = handle ? ctx.measureText(handle).width : 0;
    const contentH = Math.max(logoImg ? logoSize : 0, fontSize);
    const contentW =
      (logoImg ? logoSize : 0) +
      (logoImg && handle ? gap : 0) +
      textW;
    const pillW = contentW + padX * 2;
    const pillH = contentH + padY * 2;

    const margin = { x: 0.04 * w, y: 0.06 * h };
    let pillX = margin.x;
    let pillY = h - margin.y - pillH;
    switch (s.watermarkPosition) {
      case "bottom-left":
        pillX = margin.x;
        pillY = h - margin.y - pillH;
        break;
      case "bottom-right":
        pillX = w - margin.x - pillW;
        pillY = h - margin.y - pillH;
        break;
      case "top-left":
        pillX = margin.x;
        pillY = margin.y;
        break;
      case "top-right":
        pillX = w - margin.x - pillW;
        pillY = margin.y;
        break;
      case "center":
        pillX = w / 2 - pillW / 2;
        pillY = h - margin.y - pillH;
        break;
    }

    // Pill background.
    ctx.fillStyle = "rgba(8,12,22,0.42)";
    roundedRect(ctx, pillX, pillY, pillW, pillH, radius);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.stroke();

    // Contents (logo first, then handle).
    let cx = pillX + padX;
    const cy = pillY + pillH / 2;
    if (logoImg) {
      const ly = cy - logoSize / 2;
      ctx.save();
      const r = logoSize / 2;
      ctx.beginPath();
      ctx.arc(cx + r, ly + r, r, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(logoImg, cx, ly, logoSize, logoSize);
      ctx.restore();
      cx += logoSize + (handle ? gap : 0);
    }
    if (handle) {
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = 6 * sc;
      ctx.shadowOffsetY = 1 * sc;
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.fillText(handle, cx, cy);
    }
    ctx.restore();
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampPx(minPx: number, vwPx: number, maxPx: number, scale: number): number {
  // The CSS min/max are in CSS pixels — convert to device pixels via `scale`.
  const min = minPx * scale;
  const max = maxPx * scale;
  // `vwPx` is already device-pixels (caller passed `Nvw * compositor.vw`).
  return Math.max(min, Math.min(max, vwPx));
}

function familyFor(family: FontFamily): string {
  switch (family) {
    case "grotesk":
      return '"Space Grotesk", "Inter", system-ui, sans-serif';
    case "display":
      return '"Bebas Neue", "Oswald", "Impact", "Arial Black", system-ui, sans-serif';
    case "condensed":
      return '"Oswald", "Arial Narrow", "Inter", system-ui, sans-serif';
    case "poster":
      return '"Anton", "Impact", "Arial Black", system-ui, sans-serif';
    case "serif":
      return '"Playfair Display", "Georgia", "Times New Roman", serif';
    case "editorial":
      return '"Cormorant Garamond", "Playfair Display", "Georgia", serif';
    case "classic":
      return '"Cinzel", "Trajan Pro", "Times New Roman", serif';
    case "mono":
      return '"JetBrains Mono", "SF Mono", ui-monospace, monospace';
    case "monoDisplay":
      return '"Major Mono Display", "JetBrains Mono", ui-monospace, monospace';
    case "sciFi":
      return '"Orbitron", "Eurostile", "Inter", system-ui, sans-serif';
    case "wide":
      return '"Audiowide", "Orbitron", system-ui, sans-serif';
    case "neon":
      return '"Monoton", "Audiowide", system-ui, sans-serif';
    case "hand":
      return '"Permanent Marker", "Comic Sans MS", cursive';
    case "sans":
    default:
      return 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
  }
}

function weightFor(w: string): number {
  switch (w) {
    case "light":
      return 300;
    case "regular":
      return 400;
    case "semibold":
      return 600;
    case "black":
      return 900;
    case "bold":
    default:
      return 700;
  }
}

function cssFont(weight: number, sizePx: number, family: string): string {
  return `${weight} ${Math.round(sizePx)}px ${family}`;
}

function applyCase(s: string, upper: boolean): string {
  if (!s) return "";
  return upper ? s.toUpperCase() : s;
}

function drawTextWithGlow(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  fill: string,
  glow?: string,
  glowBlur = 0,
): void {
  ctx.font = font;
  if (glow && glowBlur > 0) {
    ctx.shadowColor = glow;
    ctx.shadowBlur = glowBlur;
  } else {
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
  }
  ctx.fillStyle = fill;
  ctx.fillText(text, x, y);
  // Reset shadow so it doesn't leak to the next draw call.
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function roundedClip(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  roundedRect(ctx, x, y, w, h, r);
  ctx.clip();
}
